import styled, { keyframes } from "styled-components";
import COLORS from "shared/constants/colors";

const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

const Container = styled.div`
  width: 100%;
  padding: 0 20px 40px;

  @media (max-width: 768px) {
    padding: 0 12px 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  font-family: "Inter600";
  letter-spacing: 0.08em;
  color: #6b7280;
`;

const SuccessBanner = styled.div`
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  font-size: 14px;
  font-family: "Inter500";
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const OrderCard = styled.article`
  width: 100%;
  background: ${COLORS.WHITE};
  border: 1px solid ${({ $expanded }) => ($expanded ? "#d1d5db" : "#e5e7eb")};
  border-radius: 12px;
  box-shadow: ${({ $expanded }) =>
    $expanded
      ? "0 8px 24px rgba(17, 24, 39, 0.08)"
      : "0 1px 2px rgba(0, 0, 0, 0.04)"};
  overflow: hidden;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: #d1d5db;
  }
`;

const OrderCardHeader = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  padding: 20px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.BLUE};
    outline-offset: -2px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
`;

const OrderCardHeaderMain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    gap: 12px;
  }
`;

const OrderThumbStrip = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const OrderThumb = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
  border-radius: 8px;
  background: #f9fafb;
  border: 2px solid ${COLORS.WHITE};
  box-shadow: 0 0 0 1px #e5e7eb;
  margin-left: -10px;

  &:first-child {
    margin-left: 0;
  }

  &[as="div"] {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

const OrderThumbMore = styled.div`
  width: 52px;
  height: 52px;
  margin-left: -10px;
  border-radius: 8px;
  background: #f3f4f6;
  border: 2px solid ${COLORS.WHITE};
  box-shadow: 0 0 0 1px #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-family: "Inter600";
  color: #6b7280;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 11px;
  }
`;

const OrderHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const OrderTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const OrderTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const OrderIdButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 11px;
  font-family: "Inter500";
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
    border-color: #d1d5db;
  }
`;

const CopyFeedback = styled.span`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 4px;
  background: #111827;
  color: ${COLORS.WHITE};
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
`;

const OrderMeta = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;

const OrderBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const statusColors = {
  success: { bg: "#d1fae5", text: "#065f46", border: "#a7f3d0" },
  pending: { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  error: { bg: "#fee2e2", text: "#991b1b", border: "#fecaca" },
  neutral: { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" },
};

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-family: "Inter600";
  text-transform: capitalize;
  color: ${({ $tone }) => statusColors[$tone]?.text ?? statusColors.neutral.text};
  background: ${({ $tone }) => statusColors[$tone]?.bg ?? statusColors.neutral.bg};
  border: 1px solid
    ${({ $tone }) => statusColors[$tone]?.border ?? statusColors.neutral.border};
`;

const OrderHeaderPricing = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    padding-top: 12px;
    border-top: 1px solid #f3f4f6;
    gap: 8px;
  }
`;

const OrderTotal = styled.span`
  font-size: 20px;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 18px;
    flex: 1;
    text-align: left;
  }
`;

const OrderItemCount = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const ExpandButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 6px;
  border-radius: 999px;
  background: ${({ $expanded }) => ($expanded ? "#eef2ff" : "#f3f4f6")};
  color: #4b5563;
  transition: background 0.2s ease;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const OrderDetails = styled.div`
  padding: 0 20px 20px;
  border-top: 1px solid #f3f4f6;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 0 16px 16px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) repeat(3, minmax(0, 1fr));
  gap: 20px;
  padding-top: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding-top: 16px;
  }
`;

const DetailsSection = styled.section`
  min-width: 0;
`;

const SectionHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  font-size: 13px;
  font-family: "Inter600";
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OrderItem = styled.div`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;

  @media (max-width: 480px) {
    grid-template-columns: 40px minmax(0, 1fr);
    grid-template-rows: auto auto;
    gap: 8px;
    padding: 10px;
  }
`;

const OrderItemImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 6px;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const OrderItemBody = styled.div`
  min-width: 0;
`;

const OrderItemName = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    white-space: normal;
    word-break: break-word;
  }
`;

const OrderItemMeta = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
`;

const OrderItemPrice = styled.span`
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
  white-space: nowrap;

  @media (max-width: 480px) {
    grid-column: 1 / -1;
    text-align: right;
    padding-top: 4px;
    border-top: 1px solid #e5e7eb;
  }
`;

const TotalsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
`;

const TotalLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #374151;
`;

const GrandTotalLine = styled(TotalLine)`
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
  font-size: 16px;
  font-family: "Inter600";
  color: #18191f;
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
`;

const InfoLine = styled.p`
  margin: 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  display: flex;
  gap: 8px;
`;

const InfoLabel = styled.span`
  flex-shrink: 0;
  min-width: 52px;
  color: #6b7280;
  font-family: "Inter500";
`;

const InfoValue = styled.span`
  word-break: break-all;
`;

const ReceiptLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  font-family: "Inter500";
  color: ${COLORS.BLUE};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px 24px;
  text-align: center;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  @media (max-width: 768px) {
    padding: 32px 16px;
    border-radius: 8px;
  }
`;

const EmptyTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  font-family: "Inter600";
  color: #18191f;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const LoadingState = styled(EmptyState)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 6px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #9ca3af;
    animation: ${pulse} 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
`;

export {
  Container,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SuccessBanner,
  OrdersList,
  OrderCard,
  OrderCardHeader,
  OrderCardHeaderMain,
  OrderThumbStrip,
  OrderThumb,
  OrderThumbMore,
  OrderHeaderInfo,
  OrderTitleRow,
  OrderTitle,
  OrderIdButton,
  OrderMeta,
  OrderBadges,
  StatusBadge,
  OrderHeaderPricing,
  OrderTotal,
  OrderItemCount,
  ExpandButton,
  OrderDetails,
  DetailsGrid,
  DetailsSection,
  SectionHeading,
  ItemsList,
  OrderItem,
  OrderItemImage,
  OrderItemBody,
  OrderItemName,
  OrderItemMeta,
  OrderItemPrice,
  TotalsBlock,
  TotalLine,
  GrandTotalLine,
  InfoBlock,
  InfoLine,
  InfoLabel,
  InfoValue,
  ReceiptLink,
  CopyFeedback,
  EmptyState,
  EmptyTitle,
  EmptyText,
  LoadingState,
  LoadingDots,
};
